import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import styles from './styles.module.scss';

export interface Partner {
  id: string;
  name: string;
  description?: string | null;
  link?: string | null;
  banner?: string | null;
}

const bannerDecoration = new URL('../../../img/partners/banner-decoration.svg', import.meta.url).href;
const chevronDown = new URL('../../../img/icons/chevron-down.svg', import.meta.url).href;

const PartnerCard: React.FC<{ partner: Partner }> = ({ partner }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const hasDescription = Boolean(partner.description && partner.description.trim());
  const isExpanded = expanded && hasDescription;

  const bannerContent = partner.banner ? (
    <img className={styles.bannerImage} src={partner.banner} alt={partner.name} />
  ) : (
    <img className={styles.bannerDecoration} src={bannerDecoration} alt="" aria-hidden="true" />
  );

  const bannerClassName = cn(
    styles.banner,
    !partner.banner && styles.bannerPlaceholder,
    partner.link && styles.bannerLink,
  );

  return (
    <article className={styles.card}>
      {partner.link ? (
        <a
          className={bannerClassName}
          href={partner.link}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${t('partners.go')}: ${partner.name}`}
        >
          {bannerContent}
        </a>
      ) : (
        <div className={bannerClassName}>{bannerContent}</div>
      )}

      <div className={styles.content}>
        <div className={styles.headerRow}>
          <h2 className={styles.name}>{partner.name}</h2>
          {hasDescription && (
            <button
              type="button"
              className={styles.toggle}
              onClick={() => setExpanded((prev) => !prev)}
              aria-expanded={isExpanded}
              aria-label={isExpanded ? t('partners.hideText') : t('partners.showText')}
            >
              <img
                className={cn(styles.chevron, isExpanded && styles.chevronExpanded)}
                src={chevronDown}
                alt=""
                aria-hidden="true"
              />
            </button>
          )}
        </div>

        {isExpanded && (
          <>
            <p className={styles.description}>{partner.description}</p>
            {partner.link && (
              <div className={styles.actions}>
                <a className={styles.actionButton} href={partner.link} target="_blank" rel="noopener noreferrer">
                  {t('partners.go')}
                </a>
              </div>
            )}
          </>
        )}
      </div>
    </article>
  );
};

export default PartnerCard;
